/**
 * `emoteLine` — the pure composition of an {@link EmoteFeedEntry}'s display line. Split out from
 * `CommsApp.tsx`'s `FeedLine` (mirrors `commsFilter.ts`'s "no React in the derivation" pattern) so
 * the two forms are unit-testable without mounting React.
 *
 * The entry itself carries no `text` — the display name resolves **client-side** off live slot
 * presence, never off the wire — so composition happens here, fed the already-resolved `name` (via
 * `useSlotNames`/`resolveSlotName`) rather than reading a slot id off the entry itself.
 *
 * The glyph is rendered in its own `<span>` with its own font stack, so the narration text and the
 * emoji can't be one opaque string by the time they reach JSX — {@link emoteLineParts} is the pure
 * split boundary ({@link emoteLine} stays as the whole-string composition, for a consumer or test
 * that wants it).
 */
import type { EmoteFeedEntry } from "@lancer-vtt/shared";

/** The narration text and the emoji glyph, split so a renderer can give the glyph its own span. */
export type EmoteLineParts = {
  /** The narration up to and including the trailing space before the glyph. */
  text: string;
  /** The emote's glyph — always "💋" for `kiss`, the sole {@link EmoteFeedEntry}'s `emote` member. */
  emoji: string;
};

/**
 * Compose the display line for one emote entry, given the author's already-resolved display
 * name, split into the narration text and the glyph. `entry.emote` is currently always `"kiss"`
 * (a closed one-member union) — this switches on it anyway so a second emote verb is a compile
 * error here, not a silent fallthrough.
 */
export function emoteLineParts(entry: EmoteFeedEntry, name: string): EmoteLineParts {
  switch (entry.emote) {
    case "kiss":
      return entry.target !== undefined
        ? { text: `${name} kissed ${entry.target} `, emoji: "💋" }
        : { text: `${name} blew a kiss `, emoji: "💋" };
    default: {
      const _exhaustive: never = entry.emote;
      return _exhaustive;
    }
  }
}

/**
 * The full composed line (`{text}{emoji}`) — for a consumer (or a test pinning the whole string)
 * that wants one string rather than the span split {@link emoteLineParts} exists for.
 */
export function emoteLine(entry: EmoteFeedEntry, name: string): string {
  const { text, emoji } = emoteLineParts(entry, name);
  return `${text}${emoji}`;
}
